import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { ShieldIcon } from '../components/Illustrations';
import './WhyUs.css';

const WhyUs = () => {
  const { t } = useTranslation();
  const refs = useRef([]);

  const reasons = [
    { icon: '🏆', title: t('whyus.reasons.reason1.title'), desc: t('whyus.reasons.reason1.desc') },
    { icon: '💰', title: t('whyus.reasons.reason2.title'), desc: t('whyus.reasons.reason2.desc') },
    { icon: '📍', title: t('whyus.reasons.reason3.title'), desc: t('whyus.reasons.reason3.desc') },
    { icon: '🔧', title: t('whyus.reasons.reason4.title'), desc: t('whyus.reasons.reason4.desc') },
    { icon: '🛡️', title: t('whyus.reasons.reason5.title'), desc: t('whyus.reasons.reason5.desc') },
    { icon: '⚡', title: t('whyus.reasons.reason6.title'), desc: t('whyus.reasons.reason6.desc') },
    { icon: '📱', title: t('whyus.reasons.reason7.title'), desc: t('whyus.reasons.reason7.desc') },
    { icon: '🏛️', title: t('whyus.reasons.reason8.title'), desc: t('whyus.reasons.reason8.desc') },
  ];

  const process = [
    { step: '01', title: t('whyus.process.step1.title'), desc: t('whyus.process.step1.desc') },
    { step: '02', title: t('whyus.process.step2.title'), desc: t('whyus.process.step2.desc') },
    { step: '03', title: t('whyus.process.step3.title'), desc: t('whyus.process.step3.desc') },
    { step: '04', title: t('whyus.process.step4.title'), desc: t('whyus.process.step4.desc') },
    { step: '05', title: t('whyus.process.step5.title'), desc: t('whyus.process.step5.desc') },
    { step: '06', title: t('whyus.process.step6.title'), desc: t('whyus.process.step6.desc') },
  ];

  const comparisons = [
    { feature: t('whyus.comparison.features.siteSurvey'), us: true, others: t('whyus.comparison.othersValue.charged') },
    { feature: t('whyus.comparison.features.writtenQuote'), us: true, others: t('whyus.comparison.othersValue.verbal') },
    { feature: t('whyus.comparison.features.warranty'), us: true, others: false },
    { feature: t('whyus.comparison.features.certifiedTech'), us: true, others: t('whyus.comparison.othersValue.varies') },
    { feature: t('whyus.comparison.features.mobileSetup'), us: true, others: t('whyus.comparison.othersValue.extraCharge') },
    { feature: t('whyus.comparison.features.amc'), us: true, others: t('whyus.comparison.othersValue.varies') },
    { feature: t('whyus.comparison.features.gem'), us: true, others: false },
    { feature: t('whyus.comparison.features.emergency'), us: true, others: false },
  ];
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.08 }
    );
    refs.current.forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);
  const add = el => { if (el && !refs.current.includes(el)) refs.current.push(el); };

  return (
    <div>
      <section className="page-hero">
        <div className="container page-hero__inner">
          <div className="reveal" ref={add}>
            <div className="section-badge">{t('whyus.hero.badge')}</div>
            <div className="vocal-local-badge">
              <span>🇮🇳</span>
              <span className="vocal-text">{t('home.hero.vocalForLocal') || 'Vocal for Local'}</span>
              <span className="vocal-divider">|</span>
              <span className="vocal-sub">{t('home.hero.kolhapursOwn') || "Kolhapur's Own"}</span>
            </div>
            <h1>{t('whyus.hero.title1')}<br /><em style={{ fontStyle: 'italic', color: 'var(--green)' }}>{t('whyus.hero.title2')}</em></h1>
            <p style={{ color: 'var(--text-mid)', fontSize: '1.05rem', lineHeight: '1.75', maxWidth: '520px', marginTop: '1rem' }}>
              {t('whyus.hero.subtitle')}
            </p>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', flexWrap: 'wrap' }}>
              <Link to="/order" className="btn btn-green">{t('whyus.hero.getFreeQuote')}</Link>
              <Link to="/about" className="btn btn-outline">{t('whyus.hero.aboutUs')}</Link>
            </div>
          </div>
          <div className="reveal-right whyus-hero-badges" ref={add}>
            <div className="whyus-badge-big">
              <ShieldIcon size={48} color="var(--green)" />
              <div>
                <strong>{t('whyus.hero.trustedBadge')}</strong>
                <span>{t('whyus.hero.trustedSub')}</span>
              </div>
            </div>
            {[['500+', t('whyus.hero.installations')], ['10+', t('whyus.hero.yearsExperience')], ['100+', t('whyus.hero.happyClients')], ['24/7', t('whyus.hero.support')]].map(([n, l]) => (
              <div key={l} className="whyus-stat-badge">
                <span className="whyus-stat-num">{n}</span>
                <span className="whyus-stat-label">{l}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REASONS */}
      <section className="section-padding" style={{ background: 'white' }}>
        <div className="container">
          <div className="text-center reveal" ref={add} style={{ marginBottom: '3rem' }}>
            <div className="section-badge">{t('whyus.reasons.badge')}</div>
            <h2>{t('whyus.reasons.title')}</h2>
          </div>
          <div className="reasons-grid">
            {reasons.map((r, i) => (
              <div key={r.title} className="reason-card reveal" ref={add} style={{ transitionDelay: `${i * 0.08}s` }}>
                <div className="reason-icon">{r.icon}</div>
                <h3 className="reason-title">{r.title}</h3>
                <p className="reason-desc">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="section-padding" style={{ background: 'var(--blue-pale)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <div className="text-center reveal" ref={add} style={{ marginBottom: '3rem' }}>
            <div className="section-badge green">{t('whyus.comparison.badge')}</div>
            <h2>{t('whyus.comparison.title')}</h2>
          </div>
          <div className="comparison-table reveal" ref={add}>
            <div className="comparison-header">
              <div className="comparison-feature-col">{t('whyus.comparison.feature')}</div>
              <div className="comparison-us-col">{t('whyus.comparison.us')}</div>
              <div className="comparison-other-col">{t('whyus.comparison.others')}</div>
            </div>
            {comparisons.map(c => (
              <div key={c.feature} className="comparison-row">
                <div className="comparison-feature">{c.feature}</div>
                <div className="comparison-us">
                  {c.us === true ? (
                    <span className="comparison-yes">{t('whyus.comparison.yes')}</span>
                  ) : c.us}
                </div>
                <div className="comparison-other">
                  {c.others === true ? <span className="comparison-yes">✓</span>
                   : c.others === false ? <span className="comparison-no">{t('whyus.comparison.no')}</span>
                   : <span className="comparison-maybe">{c.others}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="section-padding" style={{ background: 'white' }}>
        <div className="container">
          <div className="text-center reveal" ref={add} style={{ marginBottom: '3.5rem' }}>
            <div className="section-badge">{t('whyus.process.badge')}</div>
            <h2>{t('whyus.process.title')}</h2>
            <p style={{ color: 'var(--text-mid)', marginTop: '0.5rem' }}>{t('whyus.process.subtitle')}</p>
          </div>
          <div className="process-grid">
            {process.map((p, i) => (
              <div key={p.step} className="process-card reveal" ref={add} style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="process-step-num">{p.step}</div>
                <h4 className="process-step-title">{p.title}</h4>
                <p className="process-step-desc">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ background: 'var(--navy)', padding: '5rem 0' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 style={{ color: 'white', fontFamily: 'Fraunces', fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', marginBottom: '0.8rem' }}>{t('whyus.cta.title')}</h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '2rem' }}>{t('whyus.cta.subtitle')}</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/order" className="btn btn-green">{t('whyus.cta.button1')}</Link>
            <a href="tel:+919800000000" className="btn btn-outline-white">{t('whyus.cta.button2')}</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyUs;
